import { IconButton, Tag } from "@sortera/react-components";
import { ArrowTopRightOnSquareIcon, EyeIcon, ListIcon, PlusIcon } from "@sortera/react-icons";
import type { ProjectDetailsDto } from "../../types/index.ts";
import type { PreviewState } from "../preview-panel/index.ts";
import { runText, stageText } from "./utils.ts";
import styles from "./project-header.module.css";

export interface ProjectHeaderProps {
  project: ProjectDetailsDto | null;
  title: string | null;
  onOpenMenu: () => void;
  onNewConversation: () => void;
  previewOpen: boolean;
  previewState: PreviewState;
  /** Absent when the preview is switched off: then there is no button for it. */
  onTogglePreview?: () => void;
}

export function ProjectHeader({
  project,
  title,
  onOpenMenu,
  onNewConversation,
  previewOpen,
  previewState,
  onTogglePreview,
}: ProjectHeaderProps) {
  const run = project?.latestRun ? runText(project.latestRun) : null;
  const testUrl = project?.testEnvironment.url ?? null;

  return (
    <header className={styles.header}>
      <span className={styles.menu}>
        <IconButton aria-label="Visa projekt och konversationer" color="secondary" size="sm" icon={<ListIcon />} onClick={onOpenMenu} />
      </span>

      <div className={styles.titles}>
        <span className={styles.project}>{project?.name ?? "Sortera Factory"}</span>
        {project && project.stage !== "unknown" && (
          <Tag size="sm" variant={project.stage === "mvp" ? "branded" : "default"} title={stageText(project.stage)}>
            {project.stage.toUpperCase()}
          </Tag>
        )}
        {title && <span className={styles.title}>/ {title}</span>}
      </div>

      <div className={styles.actions}>
        {run && project?.latestRun && (
          <a className={styles.run} href={project.latestRun.webUrl} target="_blank" rel="noreferrer" data-state={project.latestRun.state}>
            <span className={styles.dot} aria-hidden />
            {run}
          </a>
        )}
        {project && (
          <>
            {testUrl && (
              <a className={styles.chip} href={testUrl} target="_blank" rel="noreferrer">
                Testmiljön
                <ArrowTopRightOnSquareIcon size={14} />
              </a>
            )}
            <a className={`${styles.chip} ${styles.wide}`} href={project.webUrl} target="_blank" rel="noreferrer">
              Azure DevOps
              <ArrowTopRightOnSquareIcon size={14} />
            </a>
            {onTogglePreview && (
              <button
                type="button"
                className={styles.chip}
                aria-pressed={previewOpen}
                data-state={previewState.kind}
                onClick={onTogglePreview}
                aria-label="Förhandsvisning"
                title="Förhandsvisning"
              >
                <span className={styles.previewDot} aria-hidden />
                <span className={styles.previewIcon} aria-hidden>
                  <EyeIcon size={16} />
                </span>
                <span className={styles.previewLabel}>Förhandsvisning</span>
              </button>
            )}
            <IconButton aria-label="Ny konversation" title="Ny konversation" color="secondary" size="sm" icon={<PlusIcon />} onClick={onNewConversation} />
          </>
        )}
      </div>
    </header>
  );
}
